// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getMe } from "../services/users.service";
import { initAuth, trySilentRefreshOnBoot, logout as apiLogout, getAccessToken, getAuthRevision, subscribeAuthCleared } from "../services/authentication.service";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  async function boot(isMounted) {
    const revision = getAuthRevision();
    try {
      initAuth();
      await trySilentRefreshOnBoot();
      if (getAccessToken()) {
        const u = await getMe();
        if (isMounted() && revision === getAuthRevision()) setMe(u);
      } else {
        if (isMounted()) setMe(null);
      }
    } catch {
      if (isMounted() && revision === getAuthRevision()) setMe(null);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;
    const unsubscribe = subscribeAuthCleared(() => setMe(null));
    boot(() => mounted);
    return () => { mounted = false; unsubscribe(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roles = useMemo(() => {
    const r = Array.isArray(me?.roles) ? me.roles : [];
    // backend puede devolver objetos {name: "..."} o strings
    return r.map((x) => (typeof x === "string" ? x : x?.name)).filter(Boolean);
  }, [me]);

  const isAdmin = roles.includes("ADMIN");

  async function refreshMe() {
    const u = await getMe();
    setMe(u);
    return u;
  }

  async function logout() {
    await apiLogout();
    setMe(null);
    window.location.assign("/login");
  }

  const value = { me, roles, isAdmin, loading, refreshMe, logout };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
